import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../../lib/i18n";

// Scroll speed in pixels per second — slower = easier to read.
const SPEED_PX_PER_SEC = 85;
const BLANK_MS = 2500;

const TEXT_CLASS =
  "whitespace-nowrap font-body text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-black";

export default function NoticeRotator() {
  const { t, lang } = useLang();
  const MESSAGES = t("notice");
  const containerRef = useRef(null);
  const measureRef = useRef(null);

  const [index, setIndex] = useState(0);
  const [containerW, setContainerW] = useState(0);
  const [msgW, setMsgW] = useState(0);
  const [phase, setPhase] = useState("scrolling");

  // Reset index when language changes so we restart at first translated message
  useEffect(() => {
    setIndex(0);
    setPhase("scrolling");
  }, [lang]); // "scrolling" | "blank"

  // Track container width
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure current message width synchronously before paint
  useLayoutEffect(() => {
    if (phase !== "scrolling") return;
    setMsgW(0); // reset so we re-measure
    requestAnimationFrame(() => {
      if (measureRef.current) {
        setMsgW(measureRef.current.scrollWidth);
      }
    });
  }, [index, phase]);

  // Blank pause → advance to next message
  useEffect(() => {
    if (phase !== "blank") return;
    const t = setTimeout(() => {
      setIndex((i) => (i + 1) % MESSAGES.length);
      setPhase("scrolling");
    }, BLANK_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const message = MESSAGES[index];
  const ready = phase === "scrolling" && containerW > 0 && msgW > 0;
  const duration = ready ? (containerW + msgW) / SPEED_PX_PER_SEC : 0;

  return (
    <div
      ref={containerRef}
      data-testid="notice-rotator"
      className="flex-1 min-w-0 mx-4 sm:mx-8 relative overflow-hidden h-6 flex items-center"
    >
      {/* Invisible measurer */}
      {phase === "scrolling" && (
        <span ref={measureRef} aria-hidden dir="auto" className={`${TEXT_CLASS} invisible absolute`}>
          {message}
        </span>
      )}

      {/* Visible scrolling message */}
      {ready && (
        <div className="absolute inset-0 flex items-center overflow-hidden">
          <motion.div
            key={`${index}-${containerW}-${msgW}-${lang}`}
            initial={{ x: containerW }}
            animate={{ x: -msgW }}
            transition={{ duration, ease: "linear" }}
            onAnimationComplete={() => setPhase("blank")}
            dir="auto"
            className={`${TEXT_CLASS}`}
          >
            {message}
          </motion.div>
        </div>
      )}
    </div>
  );
}
